import { GenericOption } from 'pages/AutomationEngine/types';
import { TFunction } from 'react-i18next';
import {
  AssignmentType,
  AssociationSource,
  ScheduleActivityDetails,
  ScheduleActivityErrors,
  ScheduleActivityErrorsFromAPI,
  ScheduleType,
  VariableFieldResolution,
} from './types';
import {
  ASSIGNMENT_TYPES,
  ASSOCIATION_SOURCES,
  SCHEDULE_TYPES,
  VARIABLE_FIELD_RESOLUTIONS,
} from './constants';
import {
  TIMEOFFSET_HOURS_BEFORE,
  TIMEOFFSET_HOURS_AFTER,
  TIMEOFFSET_MINUTES_BEFORE,
  TIMEOFFSET_MINUTES_AFTER,
  TIMEOFFSET_SPECIFIC_TIME,
  TIMEOFFSET_FROM_FIELD,
  DateOffsetValues,
  DateOffset,
} from 'pages/AutomationEngine/helpers';
import { ActivityDetail } from '@kizen/api/activities';

export const getDateDueTypeOptions = (
  isGlobalAutomation: boolean,
  t: TFunction
) => {
  const options: GenericOption<ScheduleType>[] = [
    {
      label: t('Immediately'),
      value: SCHEDULE_TYPES.IMMEDIATELY,
    },
    {
      label: t('With Delay'),
      value: SCHEDULE_TYPES.WITH_DELAY,
    },
  ];
  if (!isGlobalAutomation) {
    options.push({
      label: t('On or Around Date'),
      value: SCHEDULE_TYPES.ON_OR_AROUND_DATE,
    });
  }
  options.push({
    label: t('On or Around Date/Time Variable'),
    value: SCHEDULE_TYPES.ON_OR_AROUND_DATE_VARIABLE,
  });
  return options;
};

export const showDateOffsetDays = (type: DateOffset | null) =>
  !!type &&
  (
    [
      DateOffsetValues.DATEOFFSET_DAYS_BEFORE,
      DateOffsetValues.DATEOFFSET_DAYS_AFTER,
    ] as DateOffset[]
  ).includes(type);

export const showTimeOffset = (type: DateOffset | null) =>
  !!type &&
  (
    [
      DateOffsetValues.DATEOFFSET_DAYS_BEFORE,
      DateOffsetValues.DATEOFFSET_DAYS_AFTER,
      DateOffsetValues.DATEOFFSET_ON_DAY,
    ] as DateOffset[]
  ).includes(type);

export const showDateOffset = (type: ScheduleType) =>
  (
    [
      SCHEDULE_TYPES.ON_OR_AROUND_DATE,
      SCHEDULE_TYPES.ON_OR_AROUND_DATE_VARIABLE,
    ] as ScheduleType[]
  ).includes(type);

export const showTimeFromField = (timeOffsetType: string | null) =>
  !!timeOffsetType && [TIMEOFFSET_FROM_FIELD].includes(timeOffsetType);

export const showTimeOffsetHoursMinutes = (type: string | null) =>
  !!type &&
  [
    TIMEOFFSET_HOURS_BEFORE,
    TIMEOFFSET_HOURS_AFTER,
    TIMEOFFSET_MINUTES_BEFORE,
    TIMEOFFSET_MINUTES_AFTER,
  ].includes(type);

export const showWithDelayTimePicker = (type: string | null) =>
  !!type && [TIMEOFFSET_SPECIFIC_TIME].includes(type);

export const getAssociationSourceOptions = (
  isContext: boolean,
  isGlobal: boolean,
  entityName: string,
  t: TFunction
) => {
  const options: GenericOption<AssociationSource>[] = [
    { value: ASSOCIATION_SOURCES.NONE, label: t('None') },
  ];

  if (!isGlobal) {
    if (isContext) {
      options.push({
        value: ASSOCIATION_SOURCES.CONTEXT_RECORD,
        label: t('This {{entityName}} record', { entityName }),
      });
    }
    options.push({
      value: ASSOCIATION_SOURCES.RELATED_FIELD,
      label: t('Primary Relationship Field'),
    });
  }

  options.push(
    {
      value: ASSOCIATION_SOURCES.RECORD_VARIABLE,
      label: t('Record from Agentic Workflow Variable'),
    },
    {
      value: ASSOCIATION_SOURCES.VARIABLE_RELATED_FIELD,
      label: t('Record Variable Primary Relationship'),
    }
  );

  return options;
};

export const activityToOption = (
  activity: SnakeToCamelCase<ActivityDetail>
): ScheduleActivityDetails['activityType'] => ({
  value: activity.id,
  label: activity.name,
  deleted: false,
  data: activity,
});

export const getAssignToTypeOptions = (
  t: TFunction,
  contextIsCustomObject: boolean = true,
  isGlobal: boolean = false
) => {
  const options: GenericOption<AssignmentType>[] = [
    {
      label: t('Round Robin (Team Member of Any Role)'),
      value: ASSIGNMENT_TYPES.SA_TYPE_ROUND_ROBIN_ALL,
    },
    {
      label: t('Round Robin (Team Member of Specific Role)'),
      value: ASSIGNMENT_TYPES.SA_TYPE_ROUND_ROBIN_ROLE,
    },
    {
      label: t('Round Robin (Select Team Members)'),
      value: ASSIGNMENT_TYPES.SA_TYPE_ROUND_ROBIN_TEAM_MEMBERS,
    },
  ];

  if (contextIsCustomObject && !isGlobal) {
    options.push({
      label: t('Owner'),
      value: ASSIGNMENT_TYPES.SA_TYPE_OWNER,
    });
  }

  options.push({
    label: t('Specific Team Member'),
    value: ASSIGNMENT_TYPES.SA_TYPE_TEAM_MEMBER,
  });

  if (!isGlobal) {
    options.push({
      label: t('Custom Team Selector Field'),
      value: ASSIGNMENT_TYPES.SA_TYPE_TEAM_MEMBER_FIELD,
    });
  }

  options.push(
    {
      label: t('Last Active Team Member (Any Role)'),
      value: ASSIGNMENT_TYPES.SA_TYPE_LAST_ACTIVE_TEAM_MEMBER,
    },
    {
      label: t('Last Active Team Member (Specific Role)'),
      value: ASSIGNMENT_TYPES.SA_TYPE_LAST_ACTIVE_TEAM_MEMBER_SPEC_ROLE,
    },
    {
      label: t('Specific Role'),
      value: ASSIGNMENT_TYPES.SA_TYPE_SPEC_ROLE,
    },
    {
      label: t('Team Member from Variable'),
      value: ASSIGNMENT_TYPES.SA_TYPE_TEAM_MEMBER_FROM_VARIABLE,
    },
    {
      label: t('Role from Variable'),
      value: ASSIGNMENT_TYPES.SA_TYPE_ROLE_FROM_VARIABLE,
    }
  );
  return options;
};

export const getVariableFieldResolutionOptions = (
  t: TFunction
): GenericOption<VariableFieldResolution>[] => [
  { value: VARIABLE_FIELD_RESOLUTIONS.OVERWRITE, label: t('Overwrite') },
  {
    value: VARIABLE_FIELD_RESOLUTIONS.UPDATE_IF_BLANK,
    label: t('Only Update Blank'),
  },
];

export const getStepErrors = (
  stepError: ScheduleActivityErrorsFromAPI | null,
  values: ScheduleActivityDetails,
  t: TFunction
): ScheduleActivityErrors => {
  stepError ??= {};

  return {
    activityType:
      stepError?.activity_type ||
      (values.activityType?.deleted
        ? [t('Activity Type is deleted')]
        : undefined),
    assignedTo: {
      assignmentType: stepError.assigned_to?.assignment_type,
      employee:
        stepError.assigned_to?.employee_id ||
        (values.assignedTo?.employee?.deleted
          ? [t('Employee is deleted')]
          : undefined),
      role:
        stepError.assigned_to?.role_id ||
        (values.assignedTo?.role?.deleted ? [t('Role is deleted')] : undefined),
      field:
        stepError.assigned_to?.field_id ||
        (values.assignedTo?.field?.deleted
          ? [t('Field is deleted')]
          : undefined),
      employees:
        stepError.assigned_to?.employee_ids ||
        (values.assignedTo?.employees?.some((e) => e.deleted)
          ? [t('One or more selected team members are deleted')]
          : undefined),
      variable:
        stepError.assigned_to?.variable ||
        (values.assignedTo?.variable?.deleted
          ? [t('Variable is deleted')]
          : undefined),
    },
    schedule: {
      type: stepError.schedule?.type,
      withDelay: {
        delayType: stepError.schedule?.with_delay?.delay_type,
        delayValueType: stepError.schedule?.with_delay?.delay_value_type,
        delayValue: stepError.schedule?.with_delay?.delay_value,
        delayVariable: values.schedule?.withDelay?.delayVariable?.deleted
          ? [t('Variable is deleted')]
          : undefined,
        timeOffset: stepError.schedule?.with_delay?.time_offset,
        timeOffsetType: stepError.schedule?.with_delay?.time_offset_type,
        timeOffsetValue: stepError.schedule?.with_delay?.time_offset_value,
        timeOffsetVariable: values.schedule?.withDelay?.timeOffsetVariable
          ? values.schedule.withDelay.timeOffsetVariable.deleted
            ? [t('Variable is deleted')]
            : undefined
          : undefined,
        time: stepError.schedule?.with_delay?.time,
      },
      onOrAroundDate: {
        fieldId: stepError.schedule?.on_or_around_date?.field_id,
        dateOffset: stepError.schedule?.on_or_around_date?.date_offset,
        dateOffsetType: stepError.schedule?.on_or_around_date?.date_offset_type,
        dateOffsetDays: stepError.schedule?.on_or_around_date?.date_offset_days,
        dateOffsetVariable:
          stepError.schedule?.on_or_around_date?.date_offset_variable ||
          (values.schedule?.onOrAroundDate?.dateOffsetVariable?.deleted
            ? [t('Variable is deleted')]
            : undefined),
        timeOffset: stepError.schedule?.on_or_around_date?.time_offset,
        time: stepError.schedule?.on_or_around_date?.time,
        timeOffsetType: stepError.schedule?.on_or_around_date?.time_offset_type,
        timeOffsetValue:
          stepError.schedule?.on_or_around_date?.time_offset_value,
        timeOffsetVariable:
          stepError.schedule?.on_or_around_date?.time_offset_variable ||
          (values.schedule?.onOrAroundDate?.timeOffsetVariable?.deleted
            ? [t('Variable is deleted')]
            : undefined),
        timeFieldId: stepError.schedule?.on_or_around_date?.time_field_id,
        variable:
          stepError.schedule?.on_or_around_date?.variable ||
          (values.schedule?.onOrAroundDate?.variable?.deleted
            ? [t('Variable is deleted')]
            : undefined),
      },
      skipNonWorkingDays: stepError.schedule?.skip_non_working_days,
    },
    associationConfigs: values.associationConfigs?.map((ac, index) => ({
      associationSource:
        stepError.association_configs?.[index]?.association_source,
      customObject:
        stepError.association_configs?.[index]?.custom_object_id ||
        (ac.customObject?.deleted
          ? [t('Custom Object is deleted')]
          : undefined),
      relationshipField:
        stepError.association_configs?.[index]?.relationship_field_id ||
        (ac.relationshipField?.deleted ? [t('Field is deleted')] : undefined),
      automationVariable:
        stepError.association_configs?.[index]?.automation_variable_name ||
        (ac.automationVariable?.deleted
          ? [t('Variable is deleted')]
          : undefined),
    })),
    notifications: values.notifications?.map((n, index) => ({
      type: stepError.notifications?.[index]?.type,
      timeAmount: stepError.notifications?.[index]?.time_amount,
      timeUnit: stepError.notifications?.[index]?.time_unit,
    })),
    associationRequiredError: stepError.association_required_error,
    targetVariable:
      stepError.target_variable ||
      (values.targetVariable?.deleted ? [t('Variable is deleted')] : undefined),
    variableFieldResolution: stepError.variable_field_resolution,
  };
};
